import React, { createContext, useContext, useState, useEffect } from "react";
import Api from "../axiosConfig";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Initialize user state from localStorage
    const savedUser = localStorage.getItem("user");
    const savedToken = localStorage.getItem("token");
    return savedUser && savedToken ? JSON.parse(savedUser) : null;
  });

  const [token, setToken] = useState(() => {
    return localStorage.getItem("token") || null;
  });

  // Update localStorage whenever user state changes
  useEffect(() => {
    if (user && token) {
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("token", token);
      // Set the token in the API configuration
      Api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      // Remove the token from API configuration
      delete Api.defaults.headers.common["Authorization"];
    }
  }, [user, token]);

  const login = async (loginData) => {
    try {
      const response = await Api.post("/v1/auth/login", loginData);
      const { success, data, message } = response.data;

      if (!success) {
        if (message && message.includes('verify')) {
          return { 
            success: false, 
            requiresVerification: true,
            message: message,
            user: data?.user 
          };
        }
        throw new Error(message || 'Login failed');
      }

      if (!data?.token || !data?.user) {
        throw new Error('Invalid response from server');
      }

      // Ensure we have valid user data
      const userInfo = data.user;
      
      // Check if roleId exists and handle it appropriately
      if (!userInfo.roleId) {
        console.error('Role information not found in user data:', userInfo);
        throw new Error('Role information not found. Please contact support.');
      }

      // Set token and user data in state
      setToken(data.token);
      setUser(userInfo);

      // Determine redirect path based on role
      const redirectPath = getRedirectPath(userInfo.roleId);
      
      return { 
        success: true, 
        user: userInfo,
        redirectTo: redirectPath
      };

    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Helper function to determine redirect path based on role
  const getRedirectPath = (roleId) => {
    // Define role IDs for easier reference
    const ROLE_IDS = {
      SUPER_ADMIN: '680b684bac470dd4183ddf92',
      ADMIN: '680b684bac470dd4183ddf93',
      MANAGER: '680b684bac470dd4183ddf94',
      STAFF: '680b684bac470dd4183ddf95',
      DRIVER: '680b684bac470dd4183ddf96',
      CUSTOMER: '680b684bac470dd4183ddf97'
    };

    // Handle both object and string roleId formats
    let roleIdValue;
    
    if (typeof roleId === 'object' && roleId !== null) {
      // If roleId is an object (populated from MongoDB), use its _id
      roleIdValue = roleId._id;
    } else {
      // Otherwise use the roleId directly
      roleIdValue = roleId;
    }

    // Determine redirect path based on role ID
    switch (roleIdValue) {
      case ROLE_IDS.SUPER_ADMIN:
      case ROLE_IDS.ADMIN:
      case ROLE_IDS.MANAGER:
      case ROLE_IDS.STAFF:
        return '/admin/dashboard';
      case ROLE_IDS.DRIVER:
        return '/driver/home';
      case ROLE_IDS.CUSTOMER:
        return '/user/home';
      default:
        console.warn('Unknown role ID:', roleIdValue);
        return '/user/home';
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    // Optional: Call logout endpoint if available
    // await Api.post("/user/logout");
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    token,
    login,
    logout,
    updateUser,
    isAuthenticated: !!(user && token),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
